import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { requestPasswordReset, confirmPasswordReset } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Forgot Password — PataTasks" },
      {
        name: "description",
        content: "Reset your PataTasks password securely.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [identifier, setIdentifier] = useState("");
  const [stage, setStage] = useState<"request" | "confirm">("request");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequest = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!identifier) {
      toast.error("Enter email or phone");
      return;
    }
    setLoading(true);
    try {
      const res = await requestPasswordReset(identifier.trim());
      if (res.found) {
        toast.success("Account found — enter a new password below");
        setStage("confirm");
      } else {
        toast.error("No account found with that identifier");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newPassword) {
      toast.error("Enter a new password");
      return;
    }
    setLoading(true);
    try {
      await confirmPasswordReset(identifier.trim(), newPassword);
      toast.success("Password updated. Please login.");
      navigate({ to: "/login" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-slate-50 via-blue-50/30 to-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-linear-to-br from-primary to-blue-600 text-primary-foreground shadow-xl">
            <img
              src="/patataskslogo.png"
              alt="PataTasks Logo"
              className="h-12 w-12 rounded-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Reset Password</h1>
          <p className="mt-1 text-sm text-slate-500">
            Enter your email or phone to reset your password
          </p>
        </div>

        {stage === "request" && (
          <form
            onSubmit={handleRequest}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <div className="mb-4">
              <Label htmlFor="identifier" className="mb-1.5 text-slate-700">
                Email or Phone
              </Label>
              <Input
                id="identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@example.com or 0712345678"
                className="bg-slate-50"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Checking..." : "Find Account"}
            </Button>
          </form>
        )}

        {stage === "confirm" && (
          <form
            onSubmit={handleConfirm}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <div className="mb-4">
              <Label htmlFor="newPassword" className="mb-1.5 text-slate-700">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-slate-50 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2 top-2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Updating..." : "Set New Password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
